import { FC, memo } from "react";
import { FromAccountProps } from "./types";
import {
  Box,
  Card,
  createStyles,
  Group,
  Image,
  Loader,
  Menu,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { IconChevronDown } from "@tabler/icons-react";
import MoneySource from "./money-source/MoneySource";
import { useCheckSourceAccountResQuery } from "@/services/dashboardAPI";
import { get } from "lodash";

import { RESULT_CODE } from "@/constants/enum";
import IconCard from "@/assets/images/pngs/Icon_Card.png";

const useStyles = createStyles((theme) => ({
  item: {
    "&[data-hovered]": {
      backgroundColor: "#e5f4fe",
      color: theme.black,
    },
  },
}));

const FromAccount: FC<FromAccountProps> = ({
  sources,
  sourceLinkId,
  onChangeSourceLinkId,
  title,
}) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const {
    data: sourceDataResponse,
    isFetching: isFetchingSourceDataResponse,
    isLoading: isLoadingSourceDataResponse,
  } = useCheckSourceAccountResQuery(
    {
      sourceLinkId: sourceLinkId.toString(),
      // sourceLinkId: "0",
    },
    {
      skip: !sourceLinkId,
    },
  );
  const isLoading = isFetchingSourceDataResponse || isLoadingSourceDataResponse;

  const handleChangeSourceLinkId = (id: number) => {
    onChangeSourceLinkId(id);
  };
  const code = get(sourceDataResponse, "body.resultCode", null);
  const selected = sources.find((item) => item.id === sourceLinkId);
  // Use getMoneySource fields directly; checkSourceAccountRes only provides balance fallback
  const displayNumber = selected?.sourceNumber || selected?.cardMask || selected?.sourceObjectNumber || "";
  const displayName = selected?.sourceName || selected?.displayName || "";
  const displayBalance =
    selected?.availableBalanceDisplay ||
    (selected?.availableBalance ? `${selected.availableBalance} VND` : null) ||
    `${get(sourceDataResponse, "body.availableBalance", "0")} VND`;
  if (sources.length === 0) {
    return (
      <Card w="100%">
        <Card.Section>
          {(title === undefined ? t("SOURCE_ACCOUNT") : title) && (
            <Title fz="lg" weight={600}>
              {title === undefined ? t("SOURCE_ACCOUNT") : title}
            </Title>
          )}
        </Card.Section>
        <Card.Section mt="md">
          <Text fz="sm" c="dimmed" py="md">
            {t("NO_SOURCE_ACCOUNT_FOUND")}
          </Text>
        </Card.Section>
      </Card>
    );
  }

  return (
    <>
      <Card w="100%">
        <Card.Section>
          {(title === undefined ? t("SOURCE_ACCOUNT") : title) && (
            <Title fz="lg" weight={600}>
              {title === undefined ? t("SOURCE_ACCOUNT") : title}
            </Title>
          )}
        </Card.Section>
        <Card.Section mt="md">
          <Group
            position="apart"
            spacing={0}
            style={{
              borderRadius: "8px",
            }}
            w="100%"
            bg="linear-gradient(279.48deg, #08AAF3 -9.02%, #2A81D0 10.15%, #10436E 99.12%)"
          >
            <Menu
              width="target"
              position="bottom"
              withinPortal
              classNames={classes}
            >
              <Menu.Target>
                <Group
                  position="apart"
                  w="100%"
                  sx={{ cursor: "pointer" }}
                  p="md"
                >
                  <Group spacing="sm">
                    <Box
                      sx={{
                        background: "#FDFDFD",
                        padding: "4px",
                        borderRadius: "50%",
                      }}
                    >
                      <Image src={IconCard} width={44} height={44} />
                    </Box>
                    <Stack spacing={0} c="white.0">
                      <Text fz="md" fw={500}>
                        {displayNumber || "—"}
                      </Text>
                      <Text fz="sm">{displayName || "—"}</Text>
                    </Stack>
                  </Group>
                  <Group spacing="xs" c="#fff">
                    {isLoading ? (
                      <Loader color="white.0" />
                    ) : (
                      <>
                        <Box
                          px="lg"
                          py="xs"
                          style={{
                            borderRadius: "36px",
                          }}
                        >
                          <Text c="#fff" fw={500} fz={16}>
                            {displayBalance || "0 VND"}
                          </Text>
                        </Box>
                        <IconChevronDown
                          size="0.9rem"
                          stroke={1.5}
                          color={theme.colors.white[0]}
                        />
                      </>
                    )}
                  </Group>
                </Group>
              </Menu.Target>
              <Menu.Dropdown
                sx={{
                  padding: 0,
                  borderRadius: 10,
                  maxHeight: 300,
                  overflowY: "auto",
                }}
              >
                {sources.map((source) => (
                  <Menu.Item
                    key={source.id}
                    sx={{
                      paddingTop: 8,
                      paddingBottom: 8,
                      borderBottom: "1px solid #EDEDED",
                    }}
                  >
                    <MoneySource
                      source={source}
                      onChange={handleChangeSourceLinkId}
                      isSelected={source.id === sourceLinkId}
                    />
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Card.Section>
      </Card>
      {!isLoading && !!sourceLinkId && code !== null && code !== RESULT_CODE.SUCCESS && (
        <Text fz={12} c="red">
          {t("FAILED_TO_FETCH_ACCOUNT_NAME")}
        </Text>
      )}
    </>
  );
};

export default memo(FromAccount);
